import ReactValidator from "legacy/ReactValidator.es6"
import NextButton from "legacy/boater-reservation-flow/NextButton"
import { isset } from "legacy/helpers.es6"
import PropTypes from "prop-types"
import React from "react"

import openWorkosAuthPopup from "src/utils/openWorkosAuthPopup"

// Note: The following globals are loaded from external scripts and not imported:
// - $ (jQuery): Used for AJAX requests
//
// Forks on window.workosAuthEnabled:
//   - on: opens AuthKit popup, hydrates state from /account/me on success.
//   - off: renders the pre-WorkOS inline email/password form that posts
//     to Devise's /users/sign_in. Both paths share the same loadBoats /
//     loadCards / setGlobalState chain.

class SignIn extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      loginValidationError: "",
      processing: false,
      email: isset(this.props.email) ? this.props.email : "",
    }
    this.validator = new ReactValidator(["email", "password"])
    this.openPopup = this.openPopup.bind(this)
    this.onAuthSuccess = this.onAuthSuccess.bind(this)
    this.onAuthError = this.onAuthError.bind(this)
    this.hydrateFromUser = this.hydrateFromUser.bind(this)
    this.loadBoats = this.loadBoats.bind(this)
    this.loadCards = this.loadCards.bind(this)
    this.setEmail = this.setEmail.bind(this)
    this.setPassword = this.setPassword.bind(this)
    this.signInBtn = this.signInBtn.bind(this)
    this.ajaxSignIn = this.ajaxSignIn.bind(this)
    this.failLogin = this.failLogin.bind(this)
  }

  componentDidMount() {
    this.props.setHeader("Log In")
  }

  componentWillUnmount() {
    this.cancelPopup?.()
  }

  // --- WorkOS popup path (window.workosAuthEnabled === true) -------------

  openPopup() {
    this.setState({ processing: true, loginValidationError: "" })
    this.cancelPopup = openWorkosAuthPopup({
      returnTo: window.location.pathname,
      onSuccess: this.onAuthSuccess,
      onError: this.onAuthError,
    })
  }

  onAuthSuccess() {
    $.get(
      process.env.BASE_URL + "/account/me",
      (data) => this.hydrateFromUser(data),
      "json"
    ).fail(() => {
      this.onAuthError(
        "Signed in, but we couldn't load your profile. Please refresh."
      )
    })
  }

  onAuthError(message) {
    this.setState({
      loginValidationError: message,
      processing: false,
    })
  }

  // --- Devise inline form path (window.workosAuthEnabled === false) ------

  setEmail(event) {
    this.setState({ email: event.target.value })
  }

  setPassword(event) {
    this.setState({ password: event.target.value })
  }

  signInBtn() {
    this.validator.displayErrors()
    this.setState({ validInputs: this.validator.allValid() })
    if (this.validator.allValid() === true) {
      this.setState({ processing: true, loginValidationError: "" })
      this.ajaxSignIn()
    }
  }

  ajaxSignIn() {
    $.post(
      process.env.BASE_URL + "/users/sign_in",
      {
        "user[email]": this.state.email,
        "user[password]": this.state.password,
      },
      (data) => {
        if (isset(data.id)) {
          this.hydrateFromUser(data)
        } else {
          this.failLogin("Error logging in, please try again")
        }
      },
      "json"
    ).fail((data) => {
      this.failLogin(JSON.parse(data.responseText).error)
    })
  }

  failLogin(error) {
    this.onAuthError(error)
  }

  // --- Shared post-sign-in hydration --------------------------------------

  hydrateFromUser(data) {
    const { useDockwaPlusPricing } = this.props
    // The popup path's /account/me returns camelCase via serialize_for_react;
    // the Devise path's /users/sign_in response is the legacy snake_case
    // shape. Read both — whichever side has the value wins.
    let {
      id: userId,
      first_name: snakeFirst,
      firstName,
      last_name: snakeLast,
      lastName,
      name: fullName,
      phone,
    } = data
    firstName = firstName || snakeFirst
    lastName = lastName || snakeLast

    if (!firstName && !lastName && fullName) {
      const components = fullName.split(" ")
      firstName = components.shift()
      lastName = components.join(" ")
    }

    this.props.setGlobalState((prevState) => ({
      userId,
      firstName,
      lastName,
      fullName,
      phone,
      user: {
        ...prevState.user,
        ...data,
      },
    }))

    const boatusId = data.boatusId || data.boatus_id
    const boatusStatus = data.boatusStatus || data.boatus_status
    if (isset(boatusId) && isset(boatusStatus)) {
      if (boatusStatus === "verified") {
        this.props.setGlobalState({ verifiedBoatUS: true })
      }
    }

    const brewerId = data.brewerId || data.brewer_id
    if (isset(brewerId)) {
      $.post(
        process.env.API_BASE_URL + "/api/1.0/validate/brewer",
        { brewer_id: brewerId },
        (brewerData) => {
          const updatedState = { verifiedBrewer: true }
          if (!useDockwaPlusPricing) {
            updatedState.promoCode = brewerData
          }
          this.props.setGlobalState(updatedState)
        }
      )
    }

    this.loadBoats()
  }

  loadBoats() {
    $.get(
      process.env.BASE_URL + "/account/boats",
      (data) => {
        if (data.length > 0) {
          this.props.setGlobalState({ userBoats: data })
        }
      },
      "json"
    ).always(() => {
      this.loadCards()
    })
  }

  loadCards() {
    $.get(
      process.env.BASE_URL + "/account/payment",
      (data) => {
        if (data.length > 0) {
          this.props.setGlobalState({
            paymentMethods: data,
            selectedPaymentMethod: data[0],
            paymentMethodCreated: "previously",
          })
        }
      },
      "json"
    ).always(() => {
      this.props.routeTo("next")
    })
  }

  renderDeviseForm() {
    return (
      <div id="signIn" className="sign-in">
        <div className="form-group">
          <label htmlFor="email">Email</label>
          <div className="form-control-with-icon">
            <i className="fa-solid fa-envelope form-control-icon-left" />
            <input
              id="email"
              className="form-control"
              value={this.state.email}
              onChange={this.setEmail}
            />
            {this.validator.message(
              "email",
              this.state.email,
              "required|email"
            )}
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="password">Password&nbsp;&nbsp;</label>
          <a
            href={process.env.BASE_URL + "/users/password/new"}
            target="_blank"
            rel="noopener noreferrer"
          >
            <em>Forgot Password?</em>
          </a>
          <div className="form-control-with-icon">
            <i className="fa-solid fa-key form-control-icon-left" />
            <input
              id="password"
              type="password"
              className="form-control"
              value={this.state.password}
              onChange={this.setPassword}
            />
            {this.validator.message(
              "password",
              this.state.password,
              "required"
            )}
            {this.validator.customMessage(this.state.loginValidationError)}
          </div>
        </div>
        <NextButton
          id="signInContinueButton"
          processing={this.state.processing}
          btnClass="btn-primary"
          click={this.signInBtn}
          icon="fa-lock"
          iconStyle="solid"
          text="Log In"
          processingText="Logging In..."
        />
      </div>
    )
  }

  renderPopupPrompt() {
    return (
      <div id="signIn" className="sign-in">
        <p className="bs-mb-4">
          Sign in to continue. We&apos;ll open a secure window for Dockwa to
          verify you, then bring you back here to finish your booking.
        </p>
        {this.state.loginValidationError && (
          <p className="text-danger bs-mb-3">
            {this.state.loginValidationError}
          </p>
        )}
        <NextButton
          id="signInContinueButton"
          processing={this.state.processing}
          btnClass="btn-primary"
          click={this.openPopup}
          icon="fa-lock"
          iconStyle="solid"
          text="Log In"
          processingText="Opening secure sign-in…"
        />
      </div>
    )
  }

  render() {
    return window.workosAuthEnabled
      ? this.renderPopupPrompt()
      : this.renderDeviseForm()
  }
}

SignIn.propTypes = {
  setHeader: PropTypes.func.isRequired,
  setGlobalState: PropTypes.func.isRequired,
  routeTo: PropTypes.func.isRequired,
  email: PropTypes.string,
  useDockwaPlusPricing: PropTypes.bool,
}

export default SignIn
